import React from 'react';
import { useHistory } from 'react-router';

import { useFeatures } from '@xingternal/hub';

import { withNWTracking } from '@/components/nwt-tracking';
import { toQuery } from '@/helpers/query-string';
import { redirectUrl } from '@/helpers/redirect-url';
import { getMatchingHighlightsFlags } from '@/helpers/tracking';
import { useBreakpoints, useLoginStates } from '@/hooks';
import type { JobRecommendationFragmentWithVisibleJob } from '@/types';

import { useSearchQuery } from '../../../contexts';
import type { JobItemResultFragmentWithVisibleJob } from '../../../types';
import type { Item } from '../results';
import { useJDPUrlParams } from './use-jdp-url-params';

import * as S from './results-list.styles';

type BannerType = 'ProJobsUpsellBanner' | 'LoggedOutBanner';

type ItemWithBanner = Item | BannerType;

export type ListType = 'SEARCH' | 'EMPTY_STATE_SEARCH' | 'EMPTY_STATE_RECOS';

type Props = {
  items: Item[];
  type?: ListType;
  isSearchFallback?: boolean;
};

const LOGGED_OUT_BANNER_POSITION = 4;
const PRO_JOBS_UPSELL_POSITION = 3;

const getItemsWithBanner = (jobs: Item[], banner: BannerType, currentPage: number): ItemWithBanner[] => {
  const bannerPosition =
    banner === 'LoggedOutBanner' ? LOGGED_OUT_BANNER_POSITION : currentPage === 1 && PRO_JOBS_UPSELL_POSITION;

  if (!bannerPosition) {
    return jobs;
  }

  if (jobs.length < bannerPosition) {
    return [...jobs, banner];
  }

  const itemsWithBanner: ItemWithBanner[] = [...jobs];
  itemsWithBanner.splice(bannerPosition - 1, 0, banner);
  return itemsWithBanner;
};

const JobTeaserListItemWithNWTTracking = withNWTracking(S.JobTeaserListItem);

export const ResultsList: React.VFC<Props> = ({ items, type = 'SEARCH', isSearchFallback = false }) => {
  const { page } = useSearchQuery();
  const { isLoggedOut, isLoggedIn } = useLoginStates();
  const { data } = useFeatures();
  const isBasic = data?.isBasic;
  const hasProJobsMembership = data?.hasProJobsMembership;
  const isRecoList = type === 'EMPTY_STATE_RECOS';
  const { isMobile } = useBreakpoints();
  const history = useHistory();

  const showLoggedOutBanner = !isMobile && isLoggedOut;
  const showProJobsUpsellBanner = isLoggedIn && !hasProJobsMembership;
  const banner = !hasProJobsMembership && !isLoggedOut ? 'ProJobsUpsellBanner' : 'LoggedOutBanner';

  const collection =
    (showProJobsUpsellBanner || showLoggedOutBanner) && !isSearchFallback
      ? getItemsWithBanner(items, banner, page)
      : items;

  const jobKey = isRecoList ? 'job' : 'jobDetail';
  const urlParams = useJDPUrlParams(type);

  return (
    <section>
      <S.List $isSearchFallback={isSearchFallback}>
        {collection.map((item: ItemWithBanner) => {
          if (item === 'ProJobsUpsellBanner') {
            return (
              <li key="proJobsUpsellBanner">
                <S.ProJobsUpsellBanner isBasic={isBasic} />
              </li>
            );
          }
          if (item === 'LoggedOutBanner') {
            return (
              <li key="loBanner">
                <S.LoggedOutBanner />
              </li>
            );
          }

          const job = item[jobKey];
          const url = `/jobs/${job.slug}`;
          const urlWithTracking = `${url}${toQuery(urlParams, { allowDots: false })}`;
          const nwtTrackingOptions = {
            page: 'jobs_search/serp',
            event_schema: 'searchresult',
            position: item.position,
            tracking_token: isRecoList
              ? (item as JobRecommendationFragmentWithVisibleJob).opTrackingTokens?.[0] ?? ''
              : (item as JobItemResultFragmentWithVisibleJob).trackingToken,
            flags: getMatchingHighlightsFlags(item.matchingHighlightsV2),
          };

          const onClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
            // we don't redirect the user if they use ctrl/option + click to open a new tab
            if (!e.metaKey && !e.ctrlKey) {
              redirectUrl(e, urlWithTracking, history);
            }
          };

          const addTrackingParams = (e: React.MouseEvent<HTMLAnchorElement>): void => {
            e.preventDefault();
            e.currentTarget.href = urlWithTracking;
          };

          const removeTrackingParams = (e: React.MouseEvent<HTMLAnchorElement>): void => {
            e.preventDefault();
            e.currentTarget.href = url;
          };

          return (
            <li key={job.id}>
              <JobTeaserListItemWithNWTTracking
                job={job}
                url={url}
                {...(!isRecoList && {
                  highlight: (item as JobItemResultFragmentWithVisibleJob).descriptionHighlight,
                })}
                isNew={item.isNew}
                nwtTrackingOptions={nwtTrackingOptions}
                matchingHighlights={item.matchingHighlightsV2}
                onClick={onClick}
                onMouseDown={addTrackingParams}
                onMouseLeave={removeTrackingParams}
              />
            </li>
          );
        })}
      </S.List>
    </section>
  );
};
